import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useChatStore } from '@/store/chat';
import { useChat } from '@/hooks/useChat';
import { chatApi } from '@/lib/api';
import { logger } from '@/lib/logger';
import MessageBubble from '@/components/chat/MessageBubble';
import MessageInput from '@/components/chat/MessageInput';
import { useAuthStore } from '@/store/auth';

export default function ChatPage() {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const thread = useChatStore((state) => 
    state.threads.find(t => t.id === threadId)
  );
  const messages = useChatStore((state) => 
    threadId ? state.messagesByThread[threadId] || [] : []
  );
  const typingIndicators = useChatStore((state) =>
    state.typingIndicators.filter(t => t.threadId === threadId && t.userId !== user?.id)
  );
  const setActiveThread = useChatStore((state) => state.setActiveThread);
  const setMessages = useChatStore((state) => state.setMessages);

  const { sendMessage, sendTypingIndicator, markAsRead } = useChat();

  useEffect(() => {
    if (!threadId) {
      navigate('/chat');
      return;
    }

    setActiveThread(threadId);
    loadMessages();

    return () => {
      setActiveThread(null);
    };
  }, [threadId]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  useEffect(() => {
    // Mark latest message as read
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.senderRole !== 'rider' && !lastMessage.readAt) {
        markAsRead(threadId!, lastMessage.id);
      }
    }
  }, [messages, threadId, markAsRead]);

  const loadMessages = async () => {
    if (!threadId) return;

    try {
      setIsLoadingMessages(true);
      const data = await chatApi.getHistory(threadId);
      setMessages(threadId, data.messages || []);
      logger.info('Messages loaded', { threadId, count: data.messages?.length || 0 });
    } catch (error) {
      logger.error('Failed to load messages', error as Error, { threadId });
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleSend = async (message: string) => {
    if (!threadId) return;
    await sendMessage(threadId, message);
  };

  const handleTyping = () => {
    if (!threadId) return;
    sendTypingIndicator(threadId);
  };

  if (!thread) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Thread not found</p>
          <button
            onClick={() => navigate('/chat')}
            className="mt-4 text-primary hover:underline"
          >
            Back to chats
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-card border-b px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate('/chat')}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <div className="flex-1">
          <h1 className="font-semibold">{thread.participants.customerName}</h1>
          <p className="text-xs text-muted-foreground">
            Order #{thread.orderId.slice(-8)}
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoadingMessages ? (
          <div className="flex items-center justify-center h-full">
            <svg className="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-center">
            <div>
              <div className="text-4xl mb-2">💬</div>
              <p className="text-muted-foreground">No messages yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Start the conversation
              </p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isOwn={message.senderRole === 'rider'}
              />
            ))}

            {/* Typing indicator */}
            {typingIndicators.length > 0 && (
              <div className="flex justify-start mb-3">
                <div className="bg-muted rounded-2xl px-4 py-3 rounded-bl-none">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <MessageInput
        onSend={handleSend}
        onTyping={handleTyping}
        disabled={isLoadingMessages}
      />
    </div>
  );
}
